import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

// Show an "Install app" affordance:
//  - Chrome / Edge / Samsung Internet: captures the beforeinstallprompt event
//    and offers a one-tap install.
//  - iOS Safari: no install API — we show a small how-to card (Share → Add to
//    Home Screen) the first time only, then remember dismissal.
export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return; // Already installed — nothing to do.

    const dismissed = localStorage.getItem("ica_pwa_dismissed") === "1";
    if (dismissed) return;

    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (isIOS) {
      setShowIOSHint(true);
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  async function install() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {}
    setDeferredPrompt(null);
  }

  function dismiss() {
    localStorage.setItem("ica_pwa_dismissed", "1");
    setDeferredPrompt(null);
    setShowIOSHint(false);
  }

  if (deferredPrompt) {
    return (
      <div
        data-testid="pwa-install-banner"
        className="mb-4 flex items-center gap-3 rounded-2xl border border-violet-500/30 bg-gradient-to-r from-violet-500/15 to-fuchsia-500/10 p-3"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-500/25 text-violet-100">
          <Download size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-white">Install Immersive</div>
          <div className="text-[11px] text-neutral-400">Get a real app icon on your home screen.</div>
        </div>
        <button
          data-testid="pwa-install-btn"
          onClick={install}
          className="btn-pill btn-primary text-xs px-3 py-1.5"
        >
          Install
        </button>
        <button
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss install prompt"
          className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 hover:bg-white/10 hover:text-white"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  if (showIOSHint) {
    return (
      <div
        data-testid="pwa-ios-hint"
        className="mb-4 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/5 text-violet-200">
          <Share size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-white">Add Immersive to your Home Screen</div>
          <div className="text-[11px] leading-snug text-neutral-400">
            Tap the <Share size={11} className="inline align-[-2px]" /> Share icon in Safari, then choose <span className="text-white">Add to Home Screen</span> to install like a real app.
          </div>
        </div>
        <button
          onClick={dismiss}
          data-testid="pwa-ios-hint-dismiss"
          aria-label="Dismiss"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-white/10 hover:text-white"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return null;
}
